import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import Icon from '@expo/vector-icons/MaterialIcons';
import { fonts } from '../utils/fonts';

const defaultColors = {
  beeYellow: '#F4C430',
  adminPageBg: '#F5F5F5',
  adminHeaderBg: '#1A1B3E',
  adminCardBg: '#FFFFFF',
  adminBorder: '#E8E8E8',
  adminMuted: '#6B6B6B',
  sidebarAccent: '#F4C430',
  sidebarTextActive: '#1A1B3E',
  danger: '#E74C3C',
};

const TITLE_LIMIT = 100;
const BODY_LIMIT = 500;

const sendBroadcastAnnouncement = httpsCallable(functions, 'sendBroadcastAnnouncement');
const previewAnnouncementAudience = httpsCallable(functions, 'previewAnnouncementAudience');

// Keep in sync with AudienceFilterType in functions/src/broadcastNotificationService.ts
type AudienceSegmentId =
  | 'all'
  | 'unverified_email'
  | 'too_few_tags'
  | 'too_many_tags'
  | 'inactive'
  | 'never_logged_in'
  | 'new_users'
  | 'notifications_disabled'
  | 'free_plan'
  | 'incomplete_profile'
  | 'no_profile_photo';

type AudienceSegment = {
  id: AudienceSegmentId;
  icon: string;
  label: string;
  description: string;
  hasTagThreshold?: boolean;
  defaultTagThreshold?: number;
  hasDays?: boolean;
  defaultDays?: number;
  daysLabel?: string;
  suggestedTitle?: string;
  suggestedBody?: string;
};

const AUDIENCE_SEGMENTS: AudienceSegment[] = [
  {
    id: 'all',
    icon: 'groups',
    label: 'Everyone',
    description: 'All users, regardless of activity or profile.',
  },
  {
    id: 'unverified_email',
    icon: 'mark-email-unread',
    label: 'Unverified Email',
    description: "Haven't confirmed their email address yet.",
    suggestedTitle: 'ኢሜይልዎን ያረጋግጡ / Please verify your email',
    suggestedBody:
      'ኢሜይልዎ እስካሁን አልተረጋገጠም መሆኑን አስተውለናል። የስራ ግጥሚያ ወይም አስፈላጊ የመለያ ማሻሻያ እንዳያመልጥዎት አሁኑኑ ያረጋግጡ።\n\nWe noticed your email isn\'t verified yet. Verify it now so you never miss a job match or an important account update.',
  },
  {
    id: 'too_few_tags',
    icon: 'label-off',
    label: 'Too Few Interests',
    description: 'Selected very few (or no) job interest tags.',
    hasTagThreshold: true,
    defaultTagThreshold: 3,
    suggestedTitle: 'የተሻለ የስራ ግጥሚያ ያግኙ / Get better job matches',
    suggestedBody:
      'ጥቂት የስራ ፍላጎቶችን ብቻ መርጠዋል። ለእርስዎ ትክክለኛ የሆኑ ስራዎችን እንድንመክርልዎ ተጨማሪ ፍላጎቶችን በቅንብሮች › የስራ ፍላጎቶች ውስጥ ይጨምሩ።\n\nYou\'ve only selected a few job interests. Add more in Settings › Job Interests so we can recommend jobs that truly fit you.',
  },
  {
    id: 'too_many_tags',
    icon: 'label-important',
    label: 'Too Many Interests',
    description: 'Selected a very broad set of job interest tags.',
    hasTagThreshold: true,
    defaultTagThreshold: 15,
    suggestedTitle: 'ፍላጎቶችዎን ይቀንሱ / Narrow down your interests',
    suggestedBody:
      'ብዙ የስራ ፍላጎቶችን መርጠዋል። በቅንብሮች › የስራ ፍላጎቶች ውስጥ ቀንሰው ማስተካከልዎ ይበልጥ ተስማሚ የስራ ግጥሚያዎችን እንድንልክልዎ ይረዳናል።\n\nYou\'ve selected a lot of job interests. Narrowing them down in Settings › Job Interests helps us send you more relevant matches.',
  },
  {
    id: 'inactive',
    icon: 'bedtime',
    label: 'Inactive Users',
    description: "Haven't logged in for a while.",
    hasDays: true,
    defaultDays: 30,
    daysLabel: "Hasn't logged in for at least",
    suggestedTitle: 'ናፍቀውናል! / We miss you!',
    suggestedBody:
      'ከመጨረሻው ጉብኝትዎ ብዙ ጊዜ ሆኖታል። አዳዲስ ስራዎች በየቀኑ ይለጠፋሉ — አዲሱን ይመልከቱ!\n\nIt\'s been a while since your last visit. New jobs are posted every day — come see what\'s new!',
  },
  {
    id: 'never_logged_in',
    icon: 'person-off',
    label: 'Never Logged In',
    description: 'Signed up but have no recorded login since.',
    suggestedTitle: 'መለያዎን ያጠናቅቁ / Finish setting up your account',
    suggestedBody:
      'መለያ ፈጥረው ነበር ነገር ግን ከዚያ ወዲህ አልተመለሱም። የስራ ፍላጎቶችዎን ለማዘጋጀት እና ከዕድሎች ጋር መገጣጠም ለመጀመር ይግቡ።\n\nYou created an account but haven\'t been back since. Log in to set your job interests and start getting matched with opportunities.',
  },
  {
    id: 'new_users',
    icon: 'fiber-new',
    label: 'New Users',
    description: 'Joined recently.',
    hasDays: true,
    defaultDays: 7,
    daysLabel: 'Joined within the last',
    suggestedTitle: 'እንኳን ደህና መጡ! 👋 / Welcome to NibJobs! 👋',
    suggestedBody:
      'ስለተቀላቀሉ እናመሰግናለን! ትክክለኛውን ዕድል ማዛመድ እንድንጀምር የስራ ፍላጎቶችዎን በቅንብሮች ውስጥ ያዘጋጁ።\n\nThanks for joining! Set your job interests in Settings so we can start matching you with the right opportunities.',
  },
  {
    id: 'notifications_disabled',
    icon: 'notifications-off',
    label: 'Notifications Off',
    description: 'Have push notifications turned off.',
    suggestedTitle: 'አዳዲስ ስራዎችን አያምልጡ / Don\'t miss new job matches',
    suggestedBody:
      'አዳዲስ ስራዎች እንደተለጠፉ ወዲያውኑ እንዲያውቁ ማሳወቂያዎችን በቅንብሮች ውስጥ ያብሩ።\n\nTurn on notifications in Settings so you hear about new jobs the moment they are posted.',
  },
  {
    id: 'free_plan',
    icon: 'workspace-premium',
    label: 'Free Plan',
    description: 'Not currently subscribed to a paid plan.',
    suggestedTitle: 'የስራ ፍለጋዎን ያሻሽሉ / Upgrade your job search',
    suggestedBody:
      'ታይነትዎን ለማሳደግ እና በስራ ፍለጋዎ ውስጥ ወደፊት ለመሄድ የፕሪሚየም ባህሪያትን ይክፈቱ። እቅዶቻችንን ዛሬውኑ ይመልከቱ!\n\nUnlock premium features to boost your visibility and get ahead in your job search. Check out our plans today!',
  },
  {
    id: 'incomplete_profile',
    icon: 'person-outline',
    label: 'Incomplete Profile',
    description: "Haven't finished setting up their profile.",
    suggestedTitle: 'መገለጫዎን ያጠናቅቁ / Complete your profile',
    suggestedBody:
      'መገለጫዎ ገና አልተጠናቀቀም። ስራ ቀጣሪዎች እንዲያዩዎት እና የተሻለ የስራ ግጥሚያ እንዲያገኙ መገለጫዎን ያጠናቅቁ።\n\nYour profile isn\'t complete yet. Finish setting it up so employers can see you and you get better job matches.',
  },
  {
    id: 'no_profile_photo',
    icon: 'no-photography',
    label: 'No Profile Photo',
    description: "Haven't uploaded a profile picture.",
    suggestedTitle: 'የመገለጫ ፎቶ ይጨምሩ / Add a profile photo',
    suggestedBody:
      'የመገለጫ ፎቶ የላችሁም። መገለጫዎ የበለጠ አስተማማኝ እንዲሆን እና ቀጣሪዎች በቀላሉ እንዲያውቁዎት የመገለጫ ፎቶ ይጨምሩ።\n\nYou don\'t have a profile photo yet. Add one to make your profile more trustworthy and help employers recognize you.',
  },
];

type HolidayTemplate = {
  id: string;
  emoji: string;
  label: string;
  dateHint: string;
  title: string;
  body: string;
};

type HolidayCategory = {
  id: string;
  label: string;
  templates: HolidayTemplate[];
};

// Bilingual (English + Amharic) templates, grouped by category. IDs match
// the holiday `key`s used by the automated cron in
// functions/src/holidays.ts — keep both files in sync when adding/editing a
// holiday. dateHint shows the fixed Ethiopian-calendar date where one
// exists; movable Christian/Muslim feasts and Nth-weekday international
// days ("2nd Sunday of May" etc.) are computed yearly, so only a
// description is shown here.
//
// This is a curated, workplace-appropriate set (major religious, national,
// and international observance days) rather than every possible calendar
// holiday — ask to add more if something's missing.
const HOLIDAY_CATEGORIES: HolidayCategory[] = [
  {
    id: 'ethiopian',
    label: 'Ethiopian Orthodox & National',
    templates: [
      {
        id: 'enkutatash',
        emoji: '🎉',
        label: 'Enkutatash (New Year)',
        dateHint: 'Meskerem 1',
        title: 'Happy Ethiopian New Year! 🎉',
        body:
          'Wishing you and your family a joyful Enkutatash! May this new year bring new opportunities, growth, and success in your career journey.\n\nእንኳን ለ አዲስ ዓመት (እንቁጣጣሽ) በሰላም አደረሳችሁ! አዲሱ ዓመት ለሁላችሁም የስራ እድገትና ስኬት የተሞላ እንዲሆን እንመኛለን።',
      },
      {
        id: 'meskel',
        emoji: '✝️',
        label: 'Meskel',
        dateHint: 'Meskerem 17',
        title: 'Happy Meskel! ✝️',
        body:
          'Happy Meskel! Wishing you light, hope, and blessings on this special day.\n\nመልካም የመስቀል በዓል! የብርሃንና የተስፋ ቀን ለሁላችሁም ይሁን።',
      },
      {
        id: 'gena',
        emoji: '🎄',
        label: 'Genna (Christmas)',
        dateHint: 'Tahsas 29',
        title: 'Merry Ethiopian Christmas! 🎄',
        body:
          'Merry Christmas! Wishing you and your loved ones a peaceful and joyful Genna filled with warmth and new beginnings.\n\nእንኳን ለገና በዓል በሰላም አደረሳችሁ! መልካም ገና!',
      },
      {
        id: 'timket',
        emoji: '💧',
        label: 'Timkat (Epiphany)',
        dateHint: 'Tir 11',
        title: 'Happy Timkat! 💧',
        body:
          'Happy Timkat! Wishing you renewal, faith, and blessings on this celebration of Epiphany.\n\nመልካም የጥምቀት በዓል! የእምነትና የበረከት ቀን ለሁላችሁም ይሆን።',
      },
      {
        id: 'hosanna',
        emoji: '🌿',
        label: 'Hosaena (Palm Sunday)',
        dateHint: 'movable date',
        title: 'Happy Hosaena! 🌿',
        body:
          'Wishing you a blessed Hosaena as Holy Week begins.\n\nመልካም የሆሳዕና በዓል! የስቅለት ሳምንት በሰላም እንዲጀምርልን።',
      },
      {
        id: 'siklet',
        emoji: '🙏',
        label: 'Siklet (Good Friday)',
        dateHint: 'movable date',
        title: 'Siklet (Good Friday) 🙏',
        body:
          'A solemn day of reflection as we observe Siklet, remembering the crucifixion.\n\nየስቅለት ዕለት፣ የጌታችንን መስቀል የምናስብበት የጸሎትና የአስተንትኖ ቀን ይሁንልን።',
      },
      {
        id: 'fasika',
        emoji: '🐣',
        label: 'Fasika (Easter)',
        dateHint: 'movable date',
        title: 'Happy Fasika! 🐣',
        body:
          'Happy Easter! Wishing you and your family a blessed Fasika filled with hope and new beginnings.\n\nእንኳን ለትንሳኤው በዓል በሰላም አደረሳችሁ! መልካም ፋሲካ!',
      },
    ],
  },
  {
    id: 'islamic',
    label: 'Islamic',
    templates: [
      {
        id: 'eidFitr',
        emoji: '🌙',
        label: 'Eid al-Fitr',
        dateHint: 'movable date',
        title: 'Eid Mubarak! 🌙',
        body:
          'Eid Mubarak! Wishing our Muslim community a blessed Eid al-Fitr filled with peace, joy, and togetherness.\n\nእንኳን ለኢድ አልፈጥር በዓል በሰላም አደረሳችሁ! እንኳን አደረሳችሁ!',
      },
      {
        id: 'eidAdha',
        emoji: '🕌',
        label: 'Eid al-Adha (Arafa)',
        dateHint: 'movable date',
        title: 'Eid al-Adha Mubarak! 🕌',
        body:
          'Eid Mubarak! Wishing our Muslim community a blessed Eid al-Adha filled with peace, generosity, and togetherness.\n\nእንኳን ለአረፋ በዓል በሰላም አደረሳችሁ!',
      },
      {
        id: 'moulid',
        emoji: '☪️',
        label: 'Mawlid',
        dateHint: 'movable date',
        title: 'Happy Mawlid! ☪️',
        body:
          "Wishing our Muslim community a peaceful Mawlid celebrating the birth of the Prophet Muhammad (PBUH).\n\nእንኳን ለመውሊድ በዓል በሰላም አደረሳችሁ!",
      },
    ],
  },
  {
    id: 'international',
    label: 'International',
    templates: [
      {
        id: 'newYear',
        emoji: '🎊',
        label: "New Year's Day",
        dateHint: 'Jan 1',
        title: 'Happy New Year! 🎊',
        body:
          'Wishing you a fresh start filled with new opportunities in the year ahead.\n\nመልካም አዲስ ዓመት! አዲሱ ዓመት አዳዲስ እድሎችን የተሞላ ይሁንላችሁ።',
      },
      {
        id: 'valentines',
        emoji: '💛',
        label: "Valentine's Day",
        dateHint: 'Feb 14',
        title: "Happy Valentine's Day! 💛",
        body:
          'Wishing you a day filled with love and appreciation for the people who matter most.\n\nመልካም የፍቅር ቀን! ለምትወዷቸው ሰዎች የፍቅር ቀን ይሁንላችሁ።',
      },
      {
        id: 'womensDay',
        emoji: '💜',
        label: "Women's Day",
        dateHint: 'Mar 8',
        title: "Happy International Women's Day! 💜",
        body:
          'Celebrating the strength, achievements, and resilience of women everywhere.\n\nየሴቶች ቀን በዓል እንኳን አደረሳችሁ! ለሴቶች ጥንካሬና ስኬት ክብር እንስጥ።',
      },
      {
        id: 'labour',
        emoji: '👷',
        label: 'Labour Day',
        dateHint: 'May 1',
        title: 'Happy Labour Day! 👷',
        body:
          "Celebrating the hard work and dedication of workers everywhere. Happy International Workers' Day!\n\nመልካም የሰራተኞች ቀን! ለሁሉም ሰራተኞች ጥረትና ተግባር ክብር ይሁን።",
      },
      {
        id: 'mothersDay',
        emoji: '🌷',
        label: "Mother's Day",
        dateHint: '2nd Sun of May',
        title: "Happy Mother's Day! 🌷",
        body:
          "To all the mothers out there — thank you for everything you do. Happy Mother's Day!\n\nመልካም የእናቶች ቀን! ለእናቶቻችን ላደረጉት ነገር ሁሉ እናመሰግናለን።",
      },
      {
        id: 'fathersDay',
        emoji: '👔',
        label: "Father's Day",
        dateHint: '3rd Sun of Jun',
        title: "Happy Father's Day! 👔",
        body:
          "To all the fathers out there — thank you for your guidance and support. Happy Father's Day!\n\nመልካም የአባቶች ቀን! ለአባቶቻችን ድጋፍና መሪነት እናመሰግናለን።",
      },
      {
        id: 'youthDay',
        emoji: '🌟',
        label: 'Youth Day',
        dateHint: 'Aug 12',
        title: 'Happy International Youth Day! 🌟',
        body:
          "Celebrating the energy, talent, and potential of young people building Ethiopia's future.\n\nመልካም የወጣቶች ቀን! የኢትዮጵያን ወደፊት እየገነቡ ላሉ ወጣቶች ሁሉ እንኳን አደረሳችሁ።",
      },
      {
        id: 'teachersDay',
        emoji: '📚',
        label: "Teachers' Day",
        dateHint: 'Oct 5',
        title: "Happy World Teachers' Day! 📚",
        body:
          "Thank you to every teacher shaping the next generation. Happy World Teachers' Day!\n\nመልካም የመምህራን ቀን! ቀጣዩን ትውልድ ለሚቀርጹ መምህራን ሁሉ እናመሰግናለን።",
      },
    ],
  },
];

export default function AnnouncementsScreen({
  colors = defaultColors,
  isDarkMode = false,
  userRole,
}: {
  colors?: any;
  isDarkMode?: boolean;
  userRole?: string;
}) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<{
    usersNotified: number;
    pushSent: number;
    pushFailed: number;
  } | null>(null);

  const [selectedAudienceId, setSelectedAudienceId] = useState<AudienceSegmentId>('all');
  const [tagThreshold, setTagThreshold] = useState(3);
  const [days, setDays] = useState(30);
  const [audiencePreview, setAudiencePreview] = useState<{
    matchedUsers: number;
    totalUsers: number;
    matchedWithPushToken: number;
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isSuperAdmin = userRole === 'superadmin';
  const canSend = isSuperAdmin && title.trim().length > 0 && body.trim().length > 0 && !sending;
  const selectedAudience = AUDIENCE_SEGMENTS.find((a) => a.id === selectedAudienceId) || AUDIENCE_SEGMENTS[0];

  const buildFilter = () => {
    const filter: { type: AudienceSegmentId; tagThreshold?: number; days?: number } = { type: selectedAudienceId };
    if (selectedAudience.hasTagThreshold) filter.tagThreshold = tagThreshold;
    if (selectedAudience.hasDays) filter.days = days;
    return filter;
  };

  // Re-estimate the audience size whenever the segment or its threshold/day
  // params change, debounced so typing in the number input doesn't fire a
  // call per keystroke.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    setPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const result = await previewAnnouncementAudience({ filter: buildFilter() });
        if (!cancelled) setAudiencePreview(result.data as any);
      } catch (error) {
        console.error('Failed to preview announcement audience:', error);
        if (!cancelled) setAudiencePreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAudienceId, tagThreshold, days, isSuperAdmin]);

  const selectAudience = (segment: AudienceSegment) => {
    setSelectedAudienceId(segment.id);
    if (segment.hasTagThreshold) setTagThreshold(segment.defaultTagThreshold ?? 3);
    if (segment.hasDays) setDays(segment.defaultDays ?? 30);
  };

  const useSuggestedMessage = (segment: AudienceSegment) => {
    if (!segment.suggestedTitle || !segment.suggestedBody) return;
    setSelectedTemplateId(null);
    setTitle(segment.suggestedTitle.slice(0, TITLE_LIMIT));
    setBody(segment.suggestedBody.slice(0, BODY_LIMIT));
  };

  // Alert.alert has no button/message UI under react-native-web (it's a
  // no-op there) — this screen runs as a web app, so route plain
  // notifications through window.alert on web and keep Alert.alert for
  // native.
  const notify = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(`${title}\n\n${message}`);
    } else {
      Alert.alert(title, message);
    }
  };

  const doSend = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const result = await sendBroadcastAnnouncement({
        title: title.trim(),
        body: body.trim(),
        filter: buildFilter(),
      });
      const data = result.data as { usersNotified: number; pushSent: number; pushFailed: number };
      setLastResult(data);
      setTitle('');
      setBody('');
      notify(
        'Announcement Sent',
        `Delivered to ${data.usersNotified} user${data.usersNotified === 1 ? '' : 's'} (${selectedAudience.label}) — ${data.pushSent} push notification${data.pushSent === 1 ? '' : 's'} sent${data.pushFailed > 0 ? `, ${data.pushFailed} failed` : ''}.`
      );
    } catch (error: any) {
      console.error('Failed to send broadcast announcement:', error);
      notify('Error', error?.message || 'Failed to send announcement. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (template: HolidayTemplate) => {
    setSelectedTemplateId(template.id);
    setTitle(template.title.slice(0, TITLE_LIMIT));
    setBody(template.body.slice(0, BODY_LIMIT));
    setSelectedAudienceId('all');
  };

  const handleSendPress = () => {
    const count = audiencePreview ? audiencePreview.matchedUsers : null;
    const countLabel = count !== null ? `${count} user${count === 1 ? '' : 's'}` : 'the matching users';
    const confirmTitle = selectedAudienceId === 'all' ? 'Send to All Users?' : `Send to "${selectedAudience.label}"?`;
    const confirmMessage = `This will push "${title.trim()}" to ${countLabel}${selectedAudienceId !== 'all' ? ` matching "${selectedAudience.label}"` : ''} right now. This can't be undone.`;

    // Alert.alert's button/onPress support is a no-op under react-native-web
    // (the admin panel runs as a web app) — window.confirm is this
    // codebase's established substitute there (see e.g. company-verification
    // approve/reject actions).
    if (Platform.OS === 'web') {
      if (window.confirm(`${confirmTitle}\n\n${confirmMessage}`)) {
        doSend();
      }
    } else {
      Alert.alert(confirmTitle, confirmMessage, [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Send', style: 'destructive', onPress: doSend },
      ]);
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.adminPageBg }}>
      {/* Page Header */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          backgroundColor: colors.adminHeaderBg,
          paddingHorizontal: 28,
          paddingVertical: 18,
          gap: 14,
        }}
      >
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            backgroundColor: `${colors.sidebarAccent}22`,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Icon name="campaign" size={22} color={colors.sidebarAccent} />
        </View>
        <View>
          <Text style={{ fontSize: 18, fontFamily: fonts.bold, fontWeight: '700', color: '#F9FAFB', letterSpacing: -0.3 }}>
            Announcements
          </Text>
          <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: '#64748B', marginTop: 1 }}>
            Push a message to everyone, or target a specific group of users
          </Text>
        </View>
      </View>

      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
        {!isSuperAdmin && (
          <View
            style={{
              backgroundColor: `${colors.danger}15`,
              borderWidth: 1,
              borderColor: `${colors.danger}40`,
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              flexDirection: 'row',
              gap: 10,
              alignItems: 'flex-start',
            }}
          >
            <Icon name="lock" size={18} color={colors.danger} />
            <Text style={{ flex: 1, fontSize: 13, fontFamily: fonts.regular, color: colors.danger }}>
              Only superadmins can send broadcast announcements. You can view this page, but the send button is disabled.
            </Text>
          </View>
        )}

        <View
          style={{
            backgroundColor: colors.adminCardBg,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: colors.adminBorder,
            padding: 16,
            marginBottom: 16,
            ...(Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: {} }) as any),
          }}
        >
          <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
            Audience
          </Text>
          <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted, marginBottom: 14 }}>
            Choose who should receive this announcement.
          </Text>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {AUDIENCE_SEGMENTS.map((segment) => {
              const isSelected = selectedAudienceId === segment.id;
              return (
                <TouchableOpacity
                  key={segment.id}
                  onPress={() => selectAudience(segment)}
                  disabled={!isSuperAdmin || sending}
                  style={{
                    width: '48%',
                    borderWidth: 1,
                    borderColor: isSelected ? colors.beeYellow : colors.adminBorder,
                    backgroundColor: isSelected ? `${colors.beeYellow}22` : colors.adminPageBg,
                    borderRadius: 10,
                    paddingHorizontal: 12,
                    paddingVertical: 10,
                  }}
                >
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <Icon name={segment.icon as any} size={15} color={isSelected ? colors.sidebarTextActive : colors.adminMuted} />
                    <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: colors.sidebarTextActive }}>
                      {segment.label}
                    </Text>
                  </View>
                  <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: colors.adminMuted }}>
                    {segment.description}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          {selectedAudience.hasTagThreshold && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14 }}>
              <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarTextActive }}>
                {selectedAudienceId === 'too_many_tags' ? 'More than' : 'Fewer than'}
              </Text>
              <TextInput
                value={String(tagThreshold)}
                onChangeText={(t) => setTagThreshold(Math.max(0, parseInt(t.replace(/[^0-9]/g, ''), 10) || 0))}
                keyboardType="number-pad"
                editable={isSuperAdmin && !sending}
                style={{
                  borderWidth: 1,
                  borderColor: colors.adminBorder,
                  borderRadius: 8,
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  fontSize: 13,
                  fontFamily: fonts.medium,
                  color: colors.sidebarTextActive,
                  minWidth: 48,
                  textAlign: 'center',
                }}
              />
              <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarTextActive }}>
                tags selected
              </Text>
            </View>
          )}

          {selectedAudience.hasDays && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14 }}>
              <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarTextActive }}>
                {selectedAudience.daysLabel}
              </Text>
              <TextInput
                value={String(days)}
                onChangeText={(t) => setDays(Math.max(1, parseInt(t.replace(/[^0-9]/g, ''), 10) || 1))}
                keyboardType="number-pad"
                editable={isSuperAdmin && !sending}
                style={{
                  borderWidth: 1,
                  borderColor: colors.adminBorder,
                  borderRadius: 8,
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  fontSize: 13,
                  fontFamily: fonts.medium,
                  color: colors.sidebarTextActive,
                  minWidth: 48,
                  textAlign: 'center',
                }}
              />
              <Text style={{ fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarTextActive }}>
                days
              </Text>
            </View>
          )}

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              marginTop: 14,
              paddingTop: 14,
              borderTopWidth: 1,
              borderTopColor: colors.adminBorder,
            }}
          >
            <Icon name="groups" size={16} color={colors.adminMuted} />
            {previewLoading ? (
              <ActivityIndicator size="small" color={colors.adminMuted} />
            ) : (
              <Text style={{ fontSize: 12, fontFamily: fonts.medium, fontWeight: '500', color: colors.sidebarTextActive, flex: 1 }}>
                {audiencePreview
                  ? `${audiencePreview.matchedUsers} of ${audiencePreview.totalUsers} users match${
                      audiencePreview.matchedUsers > 0 ? ` · ${audiencePreview.matchedWithPushToken} have push enabled` : ''
                    }`
                  : 'Estimating audience…'}
              </Text>
            )}
          </View>

          {!!selectedAudience.suggestedTitle && (
            <TouchableOpacity
              onPress={() => useSuggestedMessage(selectedAudience)}
              disabled={!isSuperAdmin || sending}
              style={{ marginTop: 12, alignSelf: 'flex-start' }}
            >
              <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: colors.sidebarTextActive, textDecorationLine: 'underline' }}>
                Use suggested message for this segment
              </Text>
            </TouchableOpacity>
          )}
        </View>

        <View
          style={{
            backgroundColor: colors.adminCardBg,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: colors.adminBorder,
            padding: 16,
            marginBottom: 16,
            ...(Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: {} }) as any),
          }}
        >
          <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>
            Quick Templates · Holidays
          </Text>
          <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: colors.adminMuted, marginBottom: 14 }}>
            These are also sent automatically — see the note below.
          </Text>
          {HOLIDAY_CATEGORIES.map((category, categoryIndex) => (
            <View key={category.id} style={{ marginBottom: categoryIndex === HOLIDAY_CATEGORIES.length - 1 ? 0 : 16 }}>
              <Text style={{ fontSize: 11, fontFamily: fonts.semiBold, fontWeight: '600', color: colors.sidebarTextActive, marginBottom: 8 }}>
                {category.label}
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8 }}>
                {category.templates.map((template) => {
                  const isSelected = selectedTemplateId === template.id;
                  return (
                    <TouchableOpacity
                      key={template.id}
                      onPress={() => applyTemplate(template)}
                      disabled={!isSuperAdmin || sending}
                      style={{
                        borderWidth: 1,
                        borderColor: isSelected ? colors.beeYellow : colors.adminBorder,
                        backgroundColor: isSelected ? `${colors.beeYellow}22` : colors.adminPageBg,
                        borderRadius: 10,
                        paddingHorizontal: 14,
                        paddingVertical: 10,
                        minWidth: 132,
                      }}
                    >
                      <Text style={{ fontSize: 20, marginBottom: 2 }}>{template.emoji}</Text>
                      <Text style={{ fontSize: 12, fontFamily: fonts.semiBold, fontWeight: '600', color: colors.sidebarTextActive }}>
                        {template.label}
                      </Text>
                      <Text style={{ fontSize: 10, fontFamily: fonts.regular, color: colors.adminMuted, marginTop: 1 }}>
                        {template.dateHint}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>
          ))}
        </View>

        <View
          style={{
            backgroundColor: `${colors.beeYellow}15`,
            borderWidth: 1,
            borderColor: `${colors.beeYellow}40`,
            borderRadius: 12,
            padding: 14,
            marginBottom: 16,
            flexDirection: 'row',
            gap: 10,
            alignItems: 'flex-start',
          }}
        >
          <Icon name="event-available" size={18} color={colors.sidebarTextActive} />
          <Text style={{ flex: 1, fontSize: 12, fontFamily: fonts.regular, color: colors.sidebarTextActive, lineHeight: 17 }}>
            All holidays above are also sent automatically — a scheduled job checks every day at 10:00 AM and pushes the matching message to all users on its own. Use this form only for one-off announcements outside that list.
          </Text>
        </View>

        <View
          style={{
            backgroundColor: colors.adminCardBg,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: colors.adminBorder,
            padding: 20,
            ...(Platform.select({ web: { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }, default: {} }) as any),
          }}
        >
          <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
            Title
          </Text>
          <TextInput
            value={title}
            onChangeText={(t) => {
              setTitle(t.slice(0, TITLE_LIMIT));
              setSelectedTemplateId(null);
            }}
            placeholder="e.g. Happy Holidays!"
            placeholderTextColor={colors.adminMuted}
            editable={isSuperAdmin && !sending}
            style={{
              borderWidth: 1,
              borderColor: colors.adminBorder,
              borderRadius: 10,
              paddingHorizontal: 14,
              paddingVertical: 12,
              fontSize: 15,
              fontFamily: fonts.regular,
              color: colors.sidebarTextActive,
              marginBottom: 4,
            }}
          />
          <Text style={{ fontSize: 11, color: colors.adminMuted, fontFamily: fonts.regular, textAlign: 'right', marginBottom: 16 }}>
            {title.length}/{TITLE_LIMIT}
          </Text>

          <Text style={{ fontSize: 12, fontFamily: fonts.bold, fontWeight: '700', color: colors.adminMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
            Message
          </Text>
          <TextInput
            value={body}
            onChangeText={(t) => {
              setBody(t.slice(0, BODY_LIMIT));
              setSelectedTemplateId(null);
            }}
            placeholder="e.g. Wishing you a joyful holiday season from the NibJobs team!"
            placeholderTextColor={colors.adminMuted}
            editable={isSuperAdmin && !sending}
            multiline
            numberOfLines={4}
            style={{
              borderWidth: 1,
              borderColor: colors.adminBorder,
              borderRadius: 10,
              paddingHorizontal: 14,
              paddingVertical: 12,
              fontSize: 15,
              fontFamily: fonts.regular,
              color: colors.sidebarTextActive,
              minHeight: 100,
              textAlignVertical: 'top',
              marginBottom: 4,
            }}
          />
          <Text style={{ fontSize: 11, color: colors.adminMuted, fontFamily: fonts.regular, textAlign: 'right', marginBottom: 20 }}>
            {body.length}/{BODY_LIMIT}
          </Text>

          <TouchableOpacity
            onPress={handleSendPress}
            disabled={!canSend}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              backgroundColor: canSend ? colors.beeYellow : colors.adminBorder,
              paddingVertical: 14,
              borderRadius: 10,
            }}
          >
            {sending ? (
              <ActivityIndicator size="small" color={colors.sidebarTextActive} />
            ) : (
              <Icon name="send" size={18} color={canSend ? colors.sidebarTextActive : colors.adminMuted} />
            )}
            <Text style={{ fontSize: 14, fontFamily: fonts.semiBold, fontWeight: '600', color: canSend ? colors.sidebarTextActive : colors.adminMuted }}>
              {sending
                ? 'Sending...'
                : `Send to ${selectedAudience.label}${audiencePreview ? ` (${audiencePreview.matchedUsers})` : ''}`}
            </Text>
          </TouchableOpacity>
        </View>

        {lastResult && (
          <View
            style={{
              backgroundColor: colors.adminCardBg,
              borderRadius: 14,
              borderWidth: 1,
              borderColor: colors.adminBorder,
              padding: 16,
              marginTop: 16,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Icon name="check-circle" size={20} color="#27AE60" />
            <Text style={{ flex: 1, fontSize: 13, fontFamily: fonts.regular, color: colors.sidebarTextActive }}>
              Last sent to {lastResult.usersNotified} user{lastResult.usersNotified === 1 ? '' : 's'} — {lastResult.pushSent} push notification{lastResult.pushSent === 1 ? '' : 's'} delivered
              {lastResult.pushFailed > 0 ? `, ${lastResult.pushFailed} failed` : ''}.
            </Text>
          </View>
        )}
      </ScrollView>
    </View>
  );
}
